import { FileOpener } from '@capacitor-community/file-opener';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { getUserLocalDatePartOnlyInIso, isWebApp } from '../lib/utils';

export const defaultExerciseFileName = 'exercise.jpeg';
export const todayExerciseFileName = `${getUserLocalDatePartOnlyInIso()}_${defaultExerciseFileName}`;

export const convertBlobOrFileToBase64 = (blob: Blob | File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const base64Data = reader.result;
			if (base64Data) {
				resolve(base64Data as string);
			}
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
};

// Extract file name from URL
export const getFileNameFromUrl = (url: string): string => {
	try {
		const decodedUrl = decodeURIComponent(url); // Handle special characters
		const urlWithoutQuery = decodedUrl.split('?')[0]; // Remove query parameters
		return urlWithoutQuery.split('/').pop() || `file-${Date.now()}`;
	} catch (error) {
		console.error('Error extracting file name:', error);
		return `file-${Date.now()}`;
	}
};

export const getFileNameOnlyFromUrl = (fileUrl: string) => {
	const fileName = getFileNameFromUrl(fileUrl);

	// Match the last two segments separated by a dot (e.g. AAA.xlsx)
	const match = fileName.match(/([^.]+)\.([^.]+)$/);
	const actualFileName = match ? match[1] : fileName;
	return actualFileName;
};

// Should map all file extensions from documentFileExtensions to MIME types
const fileExtensionToMimeType: { [key: string]: string } = {
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
	'.png': 'image/png',
	'.bmp': 'image/bmp',
	'.webp': 'image/webp',
	'.doc': 'application/msword',
	'.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'.xls': 'application/vnd.ms-excel',
	'.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'.pdf': 'application/pdf',
	'.txt': 'text/plain',
	'.pages': 'application/vnd.apple.pages',
	'.numbers': 'application/vnd.apple.numbers',
	'.key': 'application/vnd.apple.key'
};

// Helper: Infer MIME type based on file extension
export const getMimeType = (fileName: string): string => {
	const extension = fileName.toLowerCase().split('.').pop();

	return fileExtensionToMimeType[`.${extension}`] || 'application/octet-stream'; // Default fallback
};

export const writeFileToCacheDirectory = async (fileName: string, base64Data: string) => {
	return Filesystem.writeFile({
		path: fileName,
		data: base64Data,
		directory: Directory.Cache
	});
};

export const openFileFromPhone = async (fileUri: string, mimeType: string) => {
	await FileOpener.open({
		filePath: fileUri,
		contentType: mimeType,
		openWithDefault: true
	});
};

export const readFileFromDocumentsDirectory = async (filePath: string): Promise<string | null> => {
	try {
		const result = await Filesystem.readFile({
			path: filePath,
			directory: Directory.Documents
		});
		return `data:image/jpeg;base64,${result.data}`;
	} catch {
		console.log(`No local image found at ${filePath}, falling back.`);
		return null;
	}
};

export const isImageOrPdfFileLink = (url: string): boolean => {
	const decodedUrl = decodeURIComponent(url);
	const urlWithoutQuery = decodedUrl.split('?')[0];
	const fileName = urlWithoutQuery.split('/').pop()?.toLowerCase();

	// Return false if there's no valid file extension
	if (!fileName || !fileName.includes('.')) return false;

	const fileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'bmp'];
	return fileExtensions.includes(fileName.split('.').pop());
};

export const getFileType = (fileName: string): string => {
	if (!fileName) return documentFileType;
	const extension = fileName.split('.').pop()?.toLowerCase();
	const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
	return imageExtensions.includes(extension || '') ? imageFileType : documentFileType;
};

export const getFileIconType = (url: string) => {
	const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase();
	if (!ext) return '';
	if (wordExtensions.includes(ext)) return 'word';
	if (excelExtensions.includes(ext)) return 'excel';
	if (pptExtensions.includes(ext)) return 'ppt';
	if (pdfExtensions.includes(ext)) return 'pdf';
	if (appleDocExtensions.includes(ext)) return ext;
	return '';
};

export const formatBytes = (bytes: number, decimals: number = 1): string => {
	const sizes = ['Bytes', 'KB', 'MB'];
	if (bytes === 0) return '0 Bytes';
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / 1024 ** i).toFixed(decimals)} ${sizes[i]}`;
};

export const validateFiles = (
	previousFileType: string,
	currentFileType: string,
	uploadedFilesCount: number,
	files: File[]
) => {
	if (!files?.length) return 'Please add at least one file.';
	if (previousFileType && previousFileType !== currentFileType) return 'You can only upload one media type per post.';

	const maxCount = maxFileCountByType[currentFileType];
	if (uploadedFilesCount + files.length > maxCount)
		return `You can only upload a maximum of ${maxCount} ${currentFileType}${maxCount > 1 ? 's' : ''} per post.`;

	const maxFileSize = maxFileSizeByType[currentFileType || imageFileType];
	if (files.some((file) => file.size > maxFileSize)) {
		const type = currentFileType.charAt(0).toUpperCase() + currentFileType.slice(1);
		return `${type} uploads are limited to ${formatBytes(maxFileSize, 0)}.`;
	}

	if (files.some((file) => !isValidFileType(file, currentFileType))) {
		return `Invalid ${currentFileType} file type.`;
	}

	return null;
};

const isValidFileType = (file: File, type: string) => {
	if (type === imageFileType) {
		return file.type.startsWith('image/') || imageExtensions.some((ext) => file.name.endsWith(`.${ext}`));
	}
	if (type === videoFileType) {
		return file.type.startsWith('video/') || videoExtensions.some((ext) => file.name.endsWith(`.${ext}`));
	}
	if (type === documentFileType) {
		const isApplication =
			wordExtensions.some((ext) => file.name.endsWith(`.${ext}`)) ||
			excelExtensions.some((ext) => file.name.endsWith(`.${ext}`)) ||
			pptExtensions.some((ext) => file.name.endsWith(`.${ext}`)) ||
			pdfExtensions.some((ext) => file.name.endsWith(`.${ext}`));
		const isAppleDoc = appleDocExtensions.some((ext) => file.name.endsWith(ext));
		return isApplication || isAppleDoc;
	}

	return false;
};

export const downloadAndOpenPdfFile = async (pdfPath: string, onError?: (message: string) => void): Promise<void> => {
	if (isWebApp) {
		// Web: open PDF in new tab
		window.open(pdfPath, '_blank');
	} else {
		// Mobile: download and open PDF
		try {
			const fileName = getFileNameFromUrl(pdfPath);
			const response = await fetch(pdfPath);
			const blob = await response.blob();
			const base64 = await convertBlobOrFileToBase64(blob);
			const mimeType = getMimeType(fileName);

			const writeFileResult = await writeFileToCacheDirectory(fileName, base64);
			await openFileFromPhone(writeFileResult.uri, mimeType);
		} catch (error) {
			console.error('Error opening PDF:', error);
			onError?.('Failed to open PDF. Please try again.');
		}
	}
};

const maxFileSizeByType: Record<string, number> = {
	image: 30000000, // 30MB
	video: 100000000, // 100MB
	document: 35000000 // 35MB
};

const maxFileCountByType: Record<string, number> = {
	image: 10,
	video: 1,
	document: 5
};

export const imageFileType = 'image';
export const videoFileType = 'video';
export const documentFileType = 'document';

export const imageAcceptTypes = 'image/*';
export const videoAcceptTypes = 'video/*';
export const fileAcceptTypes = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pages,.numbers,.key';

// File extension lists for icon types
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv'];
const appleDocExtensions = ['pages', 'numbers', 'key'];
const wordExtensions = ['doc', 'docx'];
const excelExtensions = ['xls', 'xlsx'];
const pptExtensions = ['ppt', 'pptx'];
const pdfExtensions = ['pdf'];
